import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown as DropdownAntd, Menu } from 'antd';
import { RiGitRepositoryLine } from 'react-icons/ri';
import { TooltipTop } from '../../../../components/tooltips';
import { InPageSpinner } from '../../../../components/spinners/in-page-spinner';
import { errorAlert, successAlert } from '../../../../components/toastify/notify-toast';
import { WorkflowHandler, RepositoriesList } from '../../../../api/workflow-handler';
import { fetchRepositories, refreshActiveWorkflowTab } from '../../../../store/workflow';
import { RootState } from '../../../../store/types';

const { SubMenu } = Menu;

/*
 * Tooltip shown on the Git control when no repository is connected
 */
const noRepositoriesMessage = 'No connected repositories';

/*
 * Push/pull control for the repos connected to this workspace, surfaced directly on the canvas
 */
export const GitSyncControl: React.FC = () => {
    const dispatch = useDispatch();
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const { activeExecutionEnv } = useSelector((store: RootState) => store.CommonReducer);
    const rawGitRepositories = useSelector((store: RootState) => store.WorkflowReducer.gitRepositories) as RepositoriesList[];
    const gitRepositories = Array.isArray(rawGitRepositories) ? rawGitRepositories : [];
    const [syncingRepoId, setSyncingRepoId] = useState<number | null>(null);
     const hasRepositories = gitRepositories.length > 0;

    /*
     * Loads the connected repositories once, the first time the control is shown
     */
    useEffect(() => {
        if (!hasRepositories) {
            dispatch(fetchRepositories());
        }
    }, []);

    /*
     * Pushes or pulls the given repo, then refreshes the active canvas tab so it stops showing stale data
     */
    const syncRepo = (repo: RepositoriesList, command: 'push' | 'pull') => {
        setSyncingRepoId(repo.id);
        const data = { repoId: repo.id, env: activeExecutionEnv, command };
        WorkflowHandler.PushDirectory(`${Env?.REACT_APP_PLATFORM_URL}/git`, data, (response) => {
            setSyncingRepoId(null);
            successAlert(response);
            dispatch(refreshActiveWorkflowTab());
        }, (error) => {
            setSyncingRepoId(null);
            errorAlert(error.data);
        });
    };

    const repoMenu = (
        <Menu className="workflowItem__contextMenuOptions gitSyncMenu">
            {gitRepositories?.map(repo => (
                <SubMenu
                    key={repo.id}
                    popupClassName="gitSyncMenu__submenu"
                    title={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {repo.name}
                            {syncingRepoId === repo.id && <InPageSpinner size="Small" />}
                        </span>
                    }
                >
                    <Menu.Item disabled={syncingRepoId === repo.id} onClick={() => syncRepo(repo, 'pull')}>
                        Pull
                    </Menu.Item>
                    <Menu.Item disabled={syncingRepoId === repo.id} onClick={() => syncRepo(repo, 'push')}>
                        Push
                    </Menu.Item>
                </SubMenu>
            ))}
        </Menu>
    );

    return (
        // Tooltip attaches its hover handlers to its immediate child, which must be a real
        // DOM node for them to reach the browser - Dropdown is a React component that never
        // forwards them to the button it renders, so the tooltip silently never showed.
        <TooltipTop title={hasRepositories ? 'Git Push / Pull' : noRepositoriesMessage}>
            <span className="gitSyncCanvas__wrapper">
                <DropdownAntd overlay={repoMenu} disabled={!hasRepositories} trigger={['click']}>
                    <button id="gitSyncCanvas_btn" disabled={!hasRepositories}>
                        <RiGitRepositoryLine fontSize={18} />
                    </button>
                </DropdownAntd>
            </span>
        </TooltipTop>
    );
};
